import { useMutation } from '@tanstack/react-query';
import { Col, DatePicker, Form, Row } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { DefaultOptionType } from 'antd/es/select';
import { CustomerApis } from 'apis/CustomerApis';
import axiosGraphQL from 'apis/GraphQL/axiosGraphQL';
import { GET_ALL_PLANT } from 'apis/GraphQL/Queries/plantQuery';
import { FormFilter, InputForm, SelectForm } from 'components';
import LazySelectForm, {
  ILazySelectData,
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import { useUser } from 'contexts/UserProvider';
import { IParamQraphQL } from 'interfaces/GraphQL/GraphQL';
import Content from 'layouts/Content';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TEAM_TYPE_OPTIONS } from 'utils/constants/TeamSale/TeamSale';
import { IFilterValues } from './types';

export const STATUS_PAYMENT_OPTIONS = [
  { label: 'Mới tạo', value: 'BP100_01' },
  { label: 'Đã Lưu', value: 'BP100_02' },
  { label: 'Khoá bảng THTT', value: 'BP100_03' },
  { label: 'Đã tạo công nợ', value: 'BP100_04' },
  { label: 'Kế toán điều chỉnh', value: 'BP100_05' },
  { label: 'Đã huỷ THTT', value: 'BP100_06' },
];

interface IProps {
  isOpen: boolean;
  defaultFilter: IFilterValues;
  onCancel: () => void;
  onFinish: (values: IFilterValues) => void;
}

const FilterPaymentSummaryV2 = (props: IProps) => {
  const { isOpen, defaultFilter, onCancel, onFinish } = props;
  const { user } = useUser();
  const [form] = useForm();
  const isAdmin = !!user?.isAdmin;
  const isHO = !!user?.isHO;

  const [optionsBranch, setOptionsBranch] = useState<DefaultOptionType[]>([]);
  const [customerData, setCustomersInRoute] =
    useState<ILazySelectData>(initLazySelectData);

  const mutationBranch = useMutation({
    mutationKey: ['getAllBranch'],
    mutationFn: (params: IParamQraphQL) =>
      axiosGraphQL.post('/graphql', params),
    onSuccess: (res) => {
      setOptionsBranch(
        res.data?.getPlant
          ? res.data?.getPlant.items.map(
              (item: { id: string }) =>
                ({
                  label: item.id,
                  value: item.id,
                }) as DefaultOptionType,
            )
          : [],
      );
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutate: getCustomersMutate, isPending: isGetCustomersMutatePending } =
    useMutation({
      mutationFn: (params: {
        pageSize: number;
        searchValue: string;
        plantId?: string;
      }) => CustomerApis.getCustomersInCombobox(params),
      onSuccess: (res) => {
        if (res?.data?.getCustomer) {
          const data = res?.data?.getCustomer;

          setCustomersInRoute({
            options: data.items.map(
              (item: {
                sapCustomerId?: string;
                storeName?: string;
                id: string;
              }) =>
                ({
                  label:
                    (item?.sapCustomerId ?? 'DMS') +
                    (item?.storeName ? ` - ${item.storeName}` : ''),
                  value: item?.id,
                }) as DefaultOptionType,
            ),
            totalItems: data?.pageInfo?.totalItems,
          });
        }
      },
    });

  // Store mutable references to avoid stale closure in useEffect
  const mutationBranchRef = useRef(mutationBranch);
  mutationBranchRef.current = mutationBranch;

  useEffect(() => {
    mutationBranchRef.current.mutate({
      query: GET_ALL_PLANT,
      variables: {
        input: {
          id: {
            not: '5000',
          },
        },
      },
    });
  }, []);

  useEffect(() => {
    form.setFieldsValue(defaultFilter);
  }, [defaultFilter, form]);

  const handleCancel = () => {
    form.resetFields();
    form.setFieldsValue(defaultFilter);
    onCancel();
  };

  const handleCustomerSelectScrollChange = useCallback(
    (params: ILazySelectParams) => {
      const { pageSize, value } = params;
      getCustomersMutate({
        pageSize,
        searchValue: value || '',
        plantId: form.getFieldValue('plantId'),
      });
    },
    [getCustomersMutate, form],
  );

  return (
    <Content isOpen={isOpen}>
      <FormFilter form={form} onFinish={onFinish} onCancel={handleCancel}>
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Form.Item label="Chi nhánh" name="plantId">
              <SelectForm
                options={optionsBranch}
                placeholder="Chọn chi nhánh"
                allowClear={isAdmin || isHO}
                disabled={!(isAdmin || isHO)}
                onChange={() => {
                  form.setFieldsValue({ customerId: undefined });
                }}
              />
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item label="Khách hàng" name="customerId">
              <LazySelectForm
                placeholder="Chọn khách hàng"
                allowClear
                data={customerData}
                loading={isGetCustomersMutatePending}
                onSelectScrollChange={handleCustomerSelectScrollChange}
              />
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item label="Mã THTT" name="id">
              <InputForm placeholder="Mã THTT" allowClear />
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item label="Thời gian tạo" name="fromDateToDate">
              <DatePicker.RangePicker
                style={{ width: '100%', height: 38 }}
                placeholder={['Từ ngày', 'Đến ngày']}
                format="DD/MM/YYYY"
              />
            </Form.Item>
          </Col>
        </Row>
      </FormFilter>
    </Content>
  );
};

export default FilterPaymentSummaryV2;
